import { haversineM } from './geo';
import type { Area, AreaSighting, CleanFix } from './types';

export interface FillerDeps {
  /** Rundowns for where the car is now, most specific first. */
  areasHere(fix: CleanFix): Area[] | Promise<Area[]>;
  /**
   * Played this trip, as a rundown or as a landmark with the same page id, or discovered on an
   * earlier trip: an area is a place, and a place plays once.
   */
  isPlayed(pageId: number): boolean | Promise<boolean>;
  /** Would an unplayed landmark trigger within this many seconds? (ProximityEngine.upcoming) */
  landmarkSoon(fix: CleanFix, withinSec: number): boolean | Promise<boolean>;
}

export interface FillerOptions {
  /** Silence needed before a rundown, counted from the last narration or the start of the trip. */
  quietMs: number;
  /** Below this the car is parked or in town traffic, where a rundown would outlast the moment. */
  minSpeedMps: number;
  /** Clear road wanted beyond the rundown's own length before starting it. */
  marginSec: number;
}

export const FILLER_DEFAULTS: FillerOptions = { quietMs: 10 * 60_000, minSpeedMps: 8, marginSec: 20 };

/** The queue state the scheduler reads; SpeechQueue satisfies it. */
export interface QueueView {
  speaking: boolean;
  queue: readonly unknown[];
  lastEndedAt: number;
}

/**
 * Picks an area rundown for a quiet stretch. Like the speech queue it runs off the location
 * fixes, never a timer: the host asks on every fix and enqueues whatever comes back as a filler
 * item. Each area plays at most once a trip because it shares the landmarks' played table.
 */
export class FillerScheduler {
  readonly opts: FillerOptions;
  private since: number | null = null;
  /** Areas the trip started in. Starting inside a county is not arriving there. */
  private startIds: Set<number> | null = null;
  /** Of those, the ones the car has since been outside of; coming back into them is an arrival. */
  private leftIds = new Set<number>();

  constructor(
    private deps: FillerDeps,
    opts: Partial<FillerOptions> = {},
  ) {
    this.opts = { ...FILLER_DEFAULTS, ...opts };
  }

  /** Start counting quiet time again, e.g. at the start of a trip. Null waits for the next fix. */
  reset(now: number | null = null): void {
    this.since = now;
    this.startIds = null;
    this.leftIds.clear();
  }

  async pick(fix: CleanFix, queue: QueueView, now: number): Promise<Area | null> {
    this.since ??= now;
    // Every fix, not only quiet ones, so leaving a start area is noticed whenever it happens.
    // Kept in memory only: after a process restart mid-trip the areas at that point count as the
    // start, which errs toward quiet.
    const here = await this.deps.areasHere(fix);
    this.observe(here);
    if (queue.speaking || queue.queue.length) return null;
    if (now - Math.max(this.since, queue.lastEndedAt) < this.opts.quietMs) return null;
    if ((fix.speedMps ?? 0) < this.opts.minSpeedMps) return null;
    for (const a of here) {
      if (this.startIds?.has(a.page_id) && !this.leftIds.has(a.page_id)) continue;
      if (await this.deps.isPlayed(a.page_id)) continue;
      // Wait rather than fall through to a shorter rundown: a landmark is about to speak anyway.
      if (await this.deps.landmarkSoon(fix, a.est_seconds + this.opts.marginSec)) return null;
      return a;
    }
    return null;
  }

  private observe(here: Area[]): void {
    const ids = new Set(here.map((a) => a.page_id));
    if (this.startIds == null) {
      this.startIds = ids;
      return;
    }
    // An empty list means no corridor call near enough (offline, or sync lagging), not "nowhere".
    if (!ids.size) return;
    for (const id of this.startIds) if (!ids.has(id)) this.leftIds.add(id);
  }

  /** Quiet time so far, for the log line when a rundown starts. */
  quietMs(queue: QueueView, now: number): number {
    return now - Math.max(this.since ?? now, queue.lastEndedAt);
  }
}

/**
 * The areas from the corridor call nearest the car, if it was made within `maxKm`. Each call
 * reports the areas of its own 0.05 degree cell, so farther than that and the list may describe
 * a county the car has already left.
 */
export function areasNear(sightings: readonly AreaSighting[], byId: ReadonlyMap<number, Area>, lat: number, lon: number, maxKm = 8): Area[] {
  let best: AreaSighting | null = null;
  let bestD = maxKm * 1000;
  for (const s of sightings) {
    const d = haversineM(lat, lon, s.lat, s.lon);
    if (d <= bestD) {
      best = s;
      bestD = d;
    }
  }
  return best ? best.page_ids.map((id) => byId.get(id)).filter((a): a is Area => a != null) : [];
}
